import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  ProductDetails,
  ServiceDetails,
} from "../../components/tables/QuotesTable";

interface GenerateQuotationPDFProps {
  quotation: {
    quotation_number: string;
    quotation_subject: string;
    quotation_type: string;
    valid_until: string;
    group_total: string;
    from_company_name: string;
    from_address: string;
    from_phone: string;
    from_email: string;
    to_client_name: string;
    to_address: string;
    to_email: string;
    terms_and_conditions: string;
  };
  details: (ProductDetails | ServiceDetails)[];
  viewType: "Product" | "Service" | null;
}

const generateQuotationPDF = async ({
  quotation,
  details,
  viewType,
}: GenerateQuotationPDFProps): Promise<Blob> => {
  const isDateExpired = (validUntil: string): boolean => {
    const [day, month, year] = validUntil.split("-").map(Number);
    const validDate = new Date(year, month - 1, day);
    const today = new Date();

    if (isNaN(validDate.getTime())) {
      console.error(`Invalid date format for validUntil: ${validUntil}`);
      return false;
    }

    validDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return validDate < today;
  };

  const isService = viewType === "Service";
  const hasIGST =
    !isService && details.some((item: any) => (item.IGST || 0) > 0);

  // Calculate totals to match the provided PDF
  const calculateTotals = () => {
    let totalSubtotal = 0;
    let totalDiscount = 0;
    let totalSGST = 0;
    let totalCGST = 0;
    let totalIGST = 0;
    let totalServiceGST = 0;
    let grandTotal = 0;

    const productTotals = details
      .filter((item: any) => isService || item.status !== 0)
      .map((item: any) => {
        const price = parseFloat(item.price || item.cost || "0");
        const qty = isService ? 1 : item.quantity || 1;
        const subtotal = price * qty;
        const discountValue = parseFloat(item.discount || "0");
        const discount =
          item.discountType === 1
            ? subtotal * (discountValue / 100)
            : discountValue;
        const taxableAmount = subtotal - discount;

        const sgstRate = parseFloat(item.SGST || "0");
        const cgstRate = parseFloat(item.CGST || "0");
        const igstRate = parseFloat(item.IGST || "0");
        const effectiveGstRate = isService
          ? parseFloat(item.service_GST || "0")
          : 0;

        const sgstAmount = !isService ? taxableAmount * (sgstRate / 100) : 0;
        const cgstAmount = !isService ? taxableAmount * (cgstRate / 100) : 0;
        const igstAmount = !isService ? taxableAmount * (igstRate / 100) : 0;
        const gstAmount = isService
          ? taxableAmount * (effectiveGstRate / 100)
          : 0;

        const total =
          taxableAmount +
          (isService ? gstAmount : sgstAmount + cgstAmount + igstAmount);

        totalSubtotal += subtotal;
        totalDiscount += discount;
        totalSGST += sgstAmount;
        totalCGST += cgstAmount;
        totalIGST += igstAmount;
        totalServiceGST += gstAmount;
        grandTotal += total;
        return {
          subtotal,
          discount,
          sgstAmount,
          cgstAmount,
          igstAmount,
          gstAmount,
          total,
        };
      });

    return {
      productTotals,
      totalSubtotal,
      totalDiscount,
      totalSGST,
      totalCGST,
      totalIGST,
      totalServiceGST,
      grandTotal: grandTotal.toFixed(2),
    };
  };

  const {
    totalSubtotal,
    totalDiscount,
    totalSGST,
    totalCGST,
    totalIGST,
    totalServiceGST,
    grandTotal,
  } = calculateTotals();

  const formattedGrandTotal = Number(grandTotal).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const currentDate = new Date()
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .split("/")
    .join("-");

  // Main content with a white background
  const mainContent = `
    <div style="font-family: 'Lato', sans-serif; margin: 0; padding: 0; background-color: #ffffff; color: #333333; line-height: 1.6;">
      <div style="max-width: 900px; background: white; border: 1px solid #e0e0e0;">
        <div style="background-color: #ffffff; color: black; padding: 20px 30px; text-align: center; display: flex; justify-content: space-between; align-items: center;">
          <h1 style="margin: 0; font-size: 26px; font-weight: 700; border-bottom: 2px solid #f4a261; display: inline-block; padding-bottom: 3px;">
            Quotation #${quotation.quotation_number}
          </h1>
          <span style="font-size: 14px; font-weight: 400; opacity: 0.9;">
            <strong>Invoice Date:</strong> ${currentDate}
          </span>
        </div>
        <div style="padding: 20px 30px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; background: #ffffff; border-bottom: 1px solid #e0e0e0;">
          <div>
            <h4 style="font-size: 16px; font-weight: 700; margin-bottom: 10px; color: #1a3c34;">From</h4>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Company Name:</span> ${quotation.from_company_name
    }
            </p>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Phone:</span> ${quotation.from_phone
    }
            </p>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Email:</span> ${quotation.from_email
    }
            </p>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Address:</span> ${quotation.from_address
    }
            </p>
          </div>
          <div>
            <h4 style="font-size: 16px; font-weight: 700; margin-bottom: 10px; color: #1a3c34;">To</h4>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Client Name:</span> ${quotation.to_client_name
    }
            </p>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Email:</span> ${quotation.to_email
    }
            </p>
            <p style="margin: 5px 0; font-size: 12px; color: #555555;">
              <span style="color: #333333; font-weight: 600; text-transform: uppercase; margin-right: 5px;">Address:</span> ${quotation.to_address
    }
            </p>
          </div>
        </div>
        <div style="padding: 20px 30px; border-bottom: 1px solid #e0e0e0;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
            <p style="margin: 5px 0; font-size: 13px; color: #555555;">
              <strong style="color: #333333; font-weight: 600;">Type:</strong> ${viewType}
            </p>
            <p style="margin: 5px 0; font-size: 13px; color: #555555;">
              <strong style="color: #333333; font-weight: 600;">Valid Till:</strong> ${quotation.valid_until
    }${isDateExpired(quotation.valid_until)
      ? '<span style="font-style: italic; color: #ff0000;"> (Expired)</span>'
      : ""
    }
            </p>
          </div>
          <div style="display: grid; grid-template-columns: 1fr; gap: 10px; margin-bottom: 10px;">
            <p style="margin: 5px 0; font-size: 13px; color: #555555;">
              <strong style="color: #333333; font-weight: 600;">Subject:</strong> ${quotation.quotation_subject
    }
            </p>
          </div>
        </div>
        <div style="padding: 20px 30px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #e0e0e0;">
            <thead>
              <tr>
                <th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 20%;">
                  ${isService ? "Service" : "Product"} Name
                </th>
                <th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 15%;">
                  Price
                </th>
                ${isService
      ? ""
      : '<th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 10%;">Quantity</th>'
    }
                <th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 15%;">
                  Discount
                </th>
                ${isService
      ? '<th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 15%;">GST</th>'
      : `
                      <th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 10%;">SGST</th>
                      <th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 10%;">CGST</th>
                      ${hasIGST
        ? '<th style="padding: 12px 15px; text-align: left; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: 10%;">IGST</th>'
        : ""
      }
                    `
    }
                <th style="padding: 12px 15px; text-align: right; background-color: #ffffff; color: #333333; font-weight: 600; font-size: 12px; text-transform: uppercase; border-bottom: 1px solid #e0e0e0; width: ${isService ? "25%" : hasIGST ? "20%" : "20%"
    };">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
${details
      .filter((item: any) => viewType === "Service" || item.status !== 0)
      .map((item: any, index: number) => {
        const price = parseFloat(item.price || item.cost || "0");
        const qty = isService ? 1 : item.quantity || 1;
        const subtotal = price * qty;
        const discountValue = parseFloat(item.discount || "0");
        const discount =
          item.discountType === 1
            ? subtotal * (discountValue / 100)
            : discountValue;
        const taxableAmount = subtotal - discount;
        const sgstRate = parseFloat(item.SGST || "0");
        const cgstRate = parseFloat(item.CGST || "0");
        const igstRate = parseFloat(item.IGST || "0");
        const effectiveGstRate = isService
          ? parseFloat(item.service_GST || "0")
          : 0;
        const sgstAmount = !isService
          ? taxableAmount * (sgstRate / 100)
          : 0;
        const cgstAmount = !isService
          ? taxableAmount * (cgstRate / 100)
          : 0;
        const igstAmount = !isService
          ? taxableAmount * (igstRate / 100)
          : 0;
        const gstAmount = isService
          ? taxableAmount * (effectiveGstRate / 100)
          : 0;
        const total =
          taxableAmount +
          (isService
            ? gstAmount
            : sgstAmount + cgstAmount + igstAmount);

        return `
                    <tr style="break-inside: avoid; page-break-inside: avoid; background-color: #ffffff;">
                      <td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">
                        ${item.name || item.title}
                      </td>
                      <td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">
                        ₹${price.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
                      </td>
                      ${isService
            ? ""
            : `<td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">${qty}</td>`
          }
                      <td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">
                        ${discountValue
            ? item.discountType === 1
              ? `${discountValue}%`
              : `₹${discountValue.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : "N/A"
          }
                      </td>
                      ${isService
            ? `<td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">${effectiveGstRate}%</td>`
            : `
                            <td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">${sgstRate}%</td>
                            <td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">${cgstRate}%</td>
                            ${hasIGST
              ? `<td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0;">${igstRate}%</td>`
              : ""
            }
                          `
          }
                      <td style="padding: 12px 15px; color: #555555; vertical-align: top; border-bottom: 1px solid #e0e0e0; text-align: right;">
                        ₹${total.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
                      </td>
                    </tr>
${item.description && item.description !== "No description available"
            ? `
      <tr style="break-inside: avoid; page-break-inside: avoid; background-color: #ffffff;">
        <td colspan="${isService ? 5 : hasIGST ? 8 : 7
            }" style="font-size: 12px; color: #333333; padding: 12px 12px; border-bottom: 1px solid #e0e0e0; text-align: justify;">
          <span style="color: #333333; font-weight: 600; text-transform: uppercase;">Description:</span> ${item.description
            }
        </td>
      </tr>
    `
            : ""
          }
                  `;
      })
      .join("")}
            </tbody>
          </table>
        </div>
        <div style="padding: 20px 30px; background: #ffffff; break-inside: avoid; page-break-inside: avoid;">
          <p style="margin: 8px 0; font-size: 13px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333;">
            <strong style="font-weight: 600;">Subtotal:</strong> ₹${totalSubtotal.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}
          </p>
          <p style="margin: 8px 0; font-size: 13px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333;">
            <strong style="font-weight: 600;">Discount:</strong> ₹${totalDiscount.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}
          </p>
          ${isService
      ? `
                <p style="margin: 8px 0; font-size: 13px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333;">
                  <strong style="font-weight: 600;">GST:</strong> ₹${totalServiceGST.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}
                </p>
              `
      : `
                <p style="margin: 8px 0; font-size: 13px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333;">
                  <strong style="font-weight: 600;">SGST:</strong> ₹${totalSGST.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}
                </p>
                <p style="margin: 8px 0; font-size: 13px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333;">
                  <strong style="font-weight: 600;">CGST:</strong> ₹${totalCGST.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}
                </p>
                ${hasIGST
        ? `
                      <p style="margin: 8px 0; font-size: 13px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333;">
                        <strong style="font-weight: 600;">Total IGST:</strong> ₹${totalIGST.toLocaleString(
          "en-IN",
          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )}
                      </p>
                    `
        : ""
      }
              `
    }
          <p style="margin: 8px 0; font-size: 15px; display: flex; justify-content: space-between; max-width: 300px; margin-left: auto; color: #333333; font-weight: 700; border-top: 1px solid #e0e0e0; padding-top: 8px;">
            <strong style="font-weight: 600;">Grand Total:</strong> ₹${formattedGrandTotal}
          </p>
        </div>
      </div>
    </div>
  `;

  // Terms content with a white background
  const termsContent =
    quotation.terms_and_conditions &&
      quotation.terms_and_conditions.trim() !== "" &&
      quotation.terms_and_conditions.trim().toUpperCase() !== "N/A"
      ? `
      <div style="font-family: 'Lato', sans-serif; background-color: #ffffff; color: #333333; line-height: 1.6; padding: 20px 30px; page-break-before: always;">
        <h1 style="margin: 0 0 12px; font-size: 16px; color: #1a3c34; font-weight: 600; border-bottom: 2px solid #f4a261; display: inline-block; padding-bottom: 3px;">
          Terms and Conditions
        </h1>
        <ul style="list-style-type: none; padding: 0; margin: 0;">
          ${quotation.terms_and_conditions
        .split("\n")
        .map(
          (term) =>
            `<li style="margin: 5px 0; color: #555555; text-align: justify;">${term}</li>`
        )
        .join("")}
        </ul>
      </div>
    `
      : `
      <div style="font-family: 'Lato', sans-serif; background-color: #ffffff; color: #333333; line-height: 1.6; padding: 20px 30px; page-break-before: always;">
        <h4 style="margin: 0 0 12px; font-size: 16px; color: #1a3c34; font-weight: 600; border-bottom: 2px solid #f4a261; display: inline-block; padding-bottom: 3px;">
          Terms and Conditions
        </h4>
        <p style="color: #555555; margin: 0; text-align: justify;">
          There are no terms and conditions for this ${isService ? "service" : "product"
      }.
        </p>
      </div>
    `;

  // Render the main content
  const mainDiv = document.createElement("div");
  mainDiv.style.position = "absolute";
  mainDiv.style.top = "-9999px";
  mainDiv.style.width = "900px";
  mainDiv.innerHTML = mainContent;
  document.body.appendChild(mainDiv);

  const mainCanvas = await html2canvas(mainDiv, {
    scale: 1,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
    width: 900,
    windowWidth: 900,
  });

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const mainImgWidth = mainCanvas.width / 2;
  const mainImgHeight = mainCanvas.height / 2;
  const mainRatio = Math.min(
    pdfWidth / mainImgWidth,
    pdfHeight / mainImgHeight
  );
  const mainScaledWidth = mainImgWidth * mainRatio;
  const mainScaledHeight = mainImgHeight * mainRatio;

  const mainImgData = mainCanvas.toDataURL("image/png");

  // Add main content to PDF
let position = 0;
const pageHeight = pdf.internal.pageSize.getHeight();
const imgHeightMm = (mainCanvas.height * pdfWidth) / mainCanvas.width;

// Convert the entire canvas height to mm (A4 scale)
while (position < imgHeightMm) {
  pdf.addImage(
    mainImgData,
    "PNG",
    0,
    -position * (mainCanvas.height / imgHeightMm),
    pdfWidth,
    imgHeightMm
  );
  position += pageHeight;
  if (position < imgHeightMm) pdf.addPage();
}


  // Clean up main content div
  document.body.removeChild(mainDiv);

  // Render the terms and conditions content on a new page
  if (termsContent) {
    pdf.addPage();

    const termsDiv = document.createElement("div");
    termsDiv.style.position = "absolute";
    termsDiv.style.top = "-9999px";
    termsDiv.style.width = "900px";
    termsDiv.innerHTML = termsContent;
    document.body.appendChild(termsDiv);

    const termsCanvas = await html2canvas(termsDiv, {
      scale: 1,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      width: 900,
      windowWidth: 900,
    });

    const termsImgWidth = termsCanvas.width / 2;
    const termsImgHeight = termsCanvas.height / 2;
    const termsRatio = Math.min(
      pdfWidth / termsImgWidth,
      pdfHeight / termsImgHeight
    );
    const termsScaledWidth = termsImgWidth * termsRatio;
    const termsScaledHeight = termsImgHeight * termsRatio;

    const termsImgData = termsCanvas.toDataURL("image/png");

    // Add terms content to PDF
    pdf.addImage(
      termsImgData,
      "PNG",
      0,
      0,
      termsScaledWidth,
      termsScaledHeight
    );

    // Handle multi-page terms content
    if (termsScaledHeight > pdfHeight) {
      const totalTermsPages = Math.ceil(termsScaledHeight / pdfHeight);
      for (let i = 1; i < totalTermsPages; i++) {
        pdf.addPage();
        pdf.addImage(
          termsImgData,
          "PNG",
          0,
          -i * pdfHeight,
          termsScaledWidth,
          termsScaledHeight
        );
      }
    }

    // Clean up terms content div
    document.body.removeChild(termsDiv);
  }

  // Add footer to every page
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(10);
    pdf.setTextColor(51, 51, 51);
    pdf.text(
      `Generated by ${quotation.from_company_name} | Contact: ${quotation.from_email}`,
      pdfWidth / 2,
      pdfHeight - 10,
      { align: "center" }
    );
  }

  // Return the PDF as a Blob instead of saving it
  return pdf.output("blob");
};

export default generateQuotationPDF;
